/**
 * The Sphere AI chat transport (MDOT-24442).
 *
 * A thin configuration of the AI SDK's `DefaultChatTransport`. sphere-mcp owns the
 * conversation (MDOT-25078): it loads the history from its own store, runs every tool,
 * and stores the turn. So a request carries only what this tab knows and the server
 * does not:
 *  - the conversation id and the user's NEW message(s) — everything after the last
 *    assistant message, including the "Continue" message that resumes a parked run;
 *  - the hidden per-turn context (what the user is viewing) and the thinking switch;
 *  - the `open_page` schema, whose page list and permission filter live in this app's
 *    router (navigation/openPageSpec.ts) — the server executes the tool from it.
 *
 * `reconnectToStream` is disabled — see below.
 */
import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { type UIMessage, type UIMessageChunk, DefaultChatTransport } from "ai";
import { readChatSettings } from "./chatSettings";
import type { SphereAIChatTool } from "./MCPHttpClient";

export type SphereAIChatTransportOptions = {
	/** The chat API base (e.g. `https://…/v1`); `/chat` is appended. */
	baseUrl: string;
	model: string;
	/** The tools whose SCHEMA this tab owns (`open_page`), read per request. */
	getTools: () => SphereAIChatTool[] | Promise<SphereAIChatTool[]>;
	/**
	 * Optional hidden context sent as the `context` field on every turn; the server
	 * appends it to the conversation tail as a `<system-reminder>`. Return `null` to add
	 * nothing.
	 */
	getContextMessage?: () => string | null;
};

class SphereAIChatTransport extends DefaultChatTransport<UIMessage> {
	/**
	 * Currently unreachable (nothing calls `chat.resumeStream()`); returning `null` keeps
	 * an enabled resume from 404ing against a server with no GET resume route.
	 */
	override async reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
		return null;
	}
}

/** The user message(s) this turn adds: everything after the last assistant message. */
export function newUserMessages(messages: UIMessage[]): UIMessage[] {
	const lastAssistant = messages.map((m) => m.role).lastIndexOf("assistant");
	return messages.slice(lastAssistant + 1).filter((m) => m.role === "user");
}

export function createSphereAIChatTransport({
	baseUrl,
	model,
	getTools,
	getContextMessage,
}: SphereAIChatTransportOptions): SphereAIChatTransport {
	return new SphereAIChatTransport({
		api: `${baseUrl}/chat`,
		headers: () => {
			const accessToken = readAtom(accessToken$);
			// Same identity headers as the MCP and upload requests: the chat route verifies
			// the bearer WITH the impersonation header.
			const impersonateEmail = readAtom(impersonateEmail$);
			return {
				...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
				...(impersonateEmail ? { "X-Impersonate-Email": impersonateEmail } : {}),
			};
		},
		prepareSendMessagesRequest: async ({ id, messages }) => ({
			body: {
				model,
				conversationId: id,
				messages: newUserMessages(messages),
				tools: await getTools(),
				context: getContextMessage?.() ?? null,
				// Read per request so flipping the switch applies to the next turn.
				thinking: readChatSettings().thinking,
			},
		}),
	});
}
